// src/render/draw.ts
import type { Renderer } from './canvas';
import { GAME_W, GAME_H } from './canvas';
import type { GameState, Tank, KillEntry } from '../sim/state';
import type { AimState } from '../input/controls';
import { drawParticles, spawnTrail } from './particles';
import { updateShake } from './shake';
import { drawLaunchFx } from './launch-fx';
import {
  HUD_Y, HUD_H,
  angleBtns, powerBtns, powerBigBtns,
  ANGLE_LABEL_X, ANGLE_LABEL_Y,
  POWER_LABEL_X, POWER_LABEL_Y,
  FIRE_X, FIRE_Y, FIRE_W, FIRE_H,
  GEAR_BTN,
  WEAPON_NAME_BTN, WEAPON_PICKER, WEAPON_ROW_H,
  type Btn,
} from './hud-layout';
import { WEAPONS, WEAPONS_BY_ID } from '../sim/weapons';
import { drawDeathBanner, type DeathBanner } from '../ui/death-quotes';

export type HudMode = 'full' | 'minimal';

export interface WeaponPickerState {
  open: boolean;
  scroll: number;
}

export interface MatchInfo {
  wins: number[];
  target: number;
}

export function drawScene(
  r: Renderer,
  state: GameState,
  dt: number,
  aim?: AimState,
  showTurnBanner = false,
  hudMode: HudMode = 'full',
  picker?: WeaponPickerState,
  match?: MatchInfo,
  deathBanner?: DeathBanner | null,
  roundEarnings?: number[] | null,
) {
  // 1. If terrain has dirty rect, putImageData the modified region
  const dirty = state.terrain.takeDirtyRect();
  if (dirty) {
    const imgData = new ImageData(
      state.terrain.buffer.data as unknown as Uint8ClampedArray<ArrayBuffer>,
      state.terrain.width,
      state.terrain.height,
    );
    r.terrainCtx.putImageData(imgData, 0, 0, dirty.x, dirty.y, dirty.w, dirty.h);
  }

  // 2. Compute shake offset
  const { dx, dy } = updateShake(dt);

  // 3. Game canvas: clear with sky color
  r.gctx.fillStyle = '#0a0e1a';
  r.gctx.fillRect(0, 0, GAME_W, GAME_H);

  // 4. Apply shake offset
  r.gctx.save();
  r.gctx.translate(dx, dy);

  // 5. Blit terrain
  r.gctx.drawImage(r.terrainCanvas, 0, 0);

  // 6. Draw each alive tank with HP bar (scaled for 960x540)
  for (const tank of state.tanks) {
    if (!tank.alive) continue;
    // Tank body: 24x12
    r.gctx.fillStyle = tank.color;
    r.gctx.fillRect(tank.x - 12, tank.y - 8, 24, 12);

    // Hit-flash overlay (FX-04)
    if (tank.flashTimer > 0) {
      r.gctx.save();
      r.gctx.globalAlpha = 0.7;
      r.gctx.fillStyle = '#fff';
      r.gctx.fillRect(tank.x - 12, tank.y - 8, 24, 12);
      r.gctx.restore();
    }

    // HP bar: 24px wide, 4px tall
    const hpRatio = tank.hp / 100;
    const barY = tank.y - 14;
    r.gctx.fillStyle = '#333';
    r.gctx.fillRect(tank.x - 12, barY, 24, 4);
    if (hpRatio > 0.5) r.gctx.fillStyle = '#0f0';
    else if (hpRatio > 0.25) r.gctx.fillStyle = '#ff0';
    else r.gctx.fillStyle = '#f00';
    r.gctx.fillRect(tank.x - 12, barY, Math.round(24 * hpRatio), 4);
  }

  // 7. Current player indicator (bobbing arrow) (TRN-02)
  const current = state.tanks[state.turn.currentPlayer];
  if (current && current.alive && state.turn.phase !== 'round-over') {
    const bobY = Math.sin(state.tick * 0.1) * 3;
    const arrowY = current.y - 26 + bobY;
    r.gctx.fillStyle = current.color;
    r.gctx.beginPath();
    r.gctx.moveTo(current.x, arrowY + 8);
    r.gctx.lineTo(current.x - 6, arrowY);
    r.gctx.lineTo(current.x + 6, arrowY);
    r.gctx.closePath();
    r.gctx.fill();
    // Player name above arrow
    r.gctx.font = 'bold 11px monospace';
    r.gctx.textAlign = 'center';
    r.gctx.fillText(current.name, current.x, arrowY - 4);
  }

  // 8. Projectiles (weapon-specific visuals with trails)
  state.projectiles.forEach(p => {
    // Spawn trail particles
    spawnTrail(p.x, p.y, p.vx, p.vy, p.weaponId);

    const def = WEAPONS_BY_ID[p.weaponId];
    const cat = def?.category ?? 'missile';
    const id = p.weaponId;

    // --- Nuke-class: differentiated by tier ---
    if (cat === 'nuke') {
      const pulse = 0.7 + Math.sin(state.tick * 0.3) * 0.3;
      if (id === 'baby-nuke') {
        // Small yellow-white glow
        r.gctx.globalAlpha = 0.35 * pulse;
        r.gctx.fillStyle = '#ff8';
        r.gctx.fillRect((p.x | 0) - 3, (p.y | 0) - 3, 10, 10);
        r.gctx.globalAlpha = 1;
        r.gctx.fillStyle = '#ffe';
        r.gctx.fillRect((p.x | 0) - 1, (p.y | 0) - 1, 6, 6);
        r.gctx.fillStyle = '#fff';
        r.gctx.fillRect((p.x | 0) + 1, (p.y | 0) + 1, 2, 2);
      } else if (id === 'nuke') {
        // Medium orange glow with visible core
        r.gctx.globalAlpha = 0.35 * pulse;
        r.gctx.fillStyle = '#fa4';
        r.gctx.fillRect((p.x | 0) - 5, (p.y | 0) - 5, 14, 14);
        r.gctx.globalAlpha = 0.55;
        r.gctx.fillStyle = '#fd6';
        r.gctx.fillRect((p.x | 0) - 3, (p.y | 0) - 3, 10, 10);
        r.gctx.globalAlpha = 1;
        r.gctx.fillStyle = '#fff';
        r.gctx.fillRect((p.x | 0) - 1, (p.y | 0) - 1, 6, 6);
        r.gctx.fillStyle = '#fe8';
        r.gctx.fillRect((p.x | 0) + 1, (p.y | 0) + 1, 2, 2);
      } else {
        // Death's Head: menacing red/orange, largest glow, skull-like dual-dot
        r.gctx.globalAlpha = 0.35 * pulse;
        r.gctx.fillStyle = '#f40';
        r.gctx.fillRect((p.x | 0) - 7, (p.y | 0) - 7, 18, 18);
        r.gctx.globalAlpha = 0.55;
        r.gctx.fillStyle = '#f84';
        r.gctx.fillRect((p.x | 0) - 4, (p.y | 0) - 4, 12, 12);
        r.gctx.globalAlpha = 1;
        r.gctx.fillStyle = '#fc4';
        r.gctx.fillRect((p.x | 0) - 2, (p.y | 0) - 2, 8, 8);
        // Skull "eyes" — two black dots
        r.gctx.fillStyle = '#000';
        r.gctx.fillRect((p.x | 0), (p.y | 0), 1, 1);
        r.gctx.fillRect((p.x | 0) + 3, (p.y | 0), 1, 1);
      }
      return;
    }

    // --- Napalm: flickering orange-red flame ---
    if (cat === 'napalm') {
      const flicker = Math.random();
      r.gctx.fillStyle = flicker > 0.5 ? '#f60' : '#f80';
      r.gctx.fillRect((p.x | 0) - 1, (p.y | 0) - 1, 5, 5);
      r.gctx.fillStyle = '#ff0';
      r.gctx.fillRect(p.x | 0, p.y | 0, 3, 3);
      return;
    }

    // --- Energy (laser/plasma): bright cyan with electric glow ---
    if (cat === 'energy') {
      r.gctx.globalAlpha = 0.4;
      r.gctx.fillStyle = '#0ff';
      r.gctx.fillRect((p.x | 0) - 3, (p.y | 0) - 3, 10, 10);
      r.gctx.globalAlpha = 1;
      r.gctx.fillStyle = '#fff';
      r.gctx.fillRect((p.x | 0) - 1, (p.y | 0) - 1, 6, 6);
      r.gctx.fillStyle = '#0ff';
      r.gctx.fillRect(p.x | 0, p.y | 0, 4, 4);
      return;
    }

    // --- Roller: larger metallic ball ---
    if (cat === 'roller') {
      r.gctx.fillStyle = '#999';
      r.gctx.fillRect((p.x | 0) - 1, (p.y | 0) - 1, 7, 7);
      r.gctx.fillStyle = '#ccc';
      r.gctx.fillRect(p.x | 0, p.y | 0, 5, 5);
      // Highlight pixel
      r.gctx.fillStyle = '#fff';
      r.gctx.fillRect((p.x | 0) + 1, (p.y | 0) + 1, 2, 2);
      return;
    }

    // --- Funky bomb subs: rainbow colored ---
    if ((cat === 'spray' || cat === 'splitter') && p.isSub) {
      const hue = (p.x * 17 + p.y * 31) % 360;
      r.gctx.fillStyle = `hsl(${hue},100%,60%)`;
      r.gctx.fillRect(p.x | 0, p.y | 0, 4, 4);
      // Sparkle
      r.gctx.fillStyle = `hsl(${(hue + 60) % 360},100%,80%)`;
      r.gctx.fillRect((p.x | 0) + 1, (p.y | 0) + 1, 2, 2);
      return;
    }

    // --- Dirt: brown lump ---
    if (cat === 'dirt') {
      r.gctx.fillStyle = '#864';
      r.gctx.fillRect(p.x | 0, p.y | 0, 5, 5);
      r.gctx.fillStyle = '#a86';
      r.gctx.fillRect((p.x | 0) + 1, (p.y | 0) + 1, 3, 3);
      return;
    }

    // --- Digger: dark metallic ---
    if (cat === 'digger') {
      r.gctx.fillStyle = p.mode === 'tunneling' ? '#666' : '#555';
      r.gctx.fillRect(p.x | 0, p.y | 0, 4, 4);
      r.gctx.fillStyle = '#888';
      r.gctx.fillRect((p.x | 0) + 1, (p.y | 0) + 1, 2, 2);
      return;
    }

    // --- Riot: red-hot projectile ---
    if (cat === 'riot') {
      r.gctx.fillStyle = '#f44';
      r.gctx.fillRect(p.x | 0, p.y | 0, 4, 4);
      r.gctx.fillStyle = '#fa8';
      r.gctx.fillRect((p.x | 0) + 1, (p.y | 0) + 1, 2, 2);
      return;
    }

    // --- Tracer: tiny dot or smoke puff ---
    if (cat === 'tracer') {
      r.gctx.fillStyle = id === 'smoke-tracer' ? '#aaa' : '#ff0';
      r.gctx.fillRect(p.x | 0, p.y | 0, 2, 2);
      return;
    }

    // --- Default missile: yellow-orange with small trail ---
    r.gctx.fillStyle = '#fc0';
    r.gctx.fillRect(p.x | 0, p.y | 0, 4, 4);
    r.gctx.fillStyle = '#fe8';
    r.gctx.fillRect((p.x | 0) + 1, (p.y | 0) + 1, 2, 2);
  });

  // 8b. Active effects (napalm/liquid dirt visual)
  if (state.activeEffects) {
    for (const e of state.activeEffects) {
      if (e.type === 'napalm') {
        // Flickering fire effect
        const flicker = Math.random();
        r.gctx.globalAlpha = 0.4;
        r.gctx.fillStyle = '#f40';
        r.gctx.fillRect((e.x | 0) - 4, (e.y | 0) - 4, 9, 9);
        r.gctx.globalAlpha = 1;
        r.gctx.fillStyle = flicker > 0.5 ? '#f80' : '#fa0';
        r.gctx.fillRect((e.x | 0) - 2, (e.y | 0) - 3, 5, 6);
        r.gctx.fillStyle = '#ff0';
        r.gctx.fillRect((e.x | 0) - 1, (e.y | 0) - 2, 3, 4);
      } else {
        // Liquid dirt: brown dripping
        r.gctx.fillStyle = '#864';
        r.gctx.fillRect((e.x | 0) - 2, (e.y | 0) - 2, 5, 5);
        r.gctx.fillStyle = '#a86';
        r.gctx.fillRect((e.x | 0) - 1, (e.y | 0) - 1, 3, 3);
      }
    }
  }

  // 9. Draw particles (additive blending)
  drawParticles(r.gctx, dt);

  // 10. Remove shake translate
  r.gctx.restore();

  // 11. Wind arrow (outside shake, scaled)
  drawWindArrow(r.gctx, state.world.wind);

  // 12. Aim line (uses current player, not tanks[0])
  if (state.aimPreview.visible && state.projectiles.activeCount === 0) {
    const aimTank = state.tanks[state.turn.currentPlayer];
    r.gctx.strokeStyle = '#fff8';
    r.gctx.lineWidth = 2;
    r.gctx.beginPath();
    r.gctx.moveTo(aimTank.x, aimTank.y);
    r.gctx.lineTo(state.aimPreview.x, state.aimPreview.y);
    r.gctx.stroke();
    r.gctx.lineWidth = 1;
  }

  // 13. Gear icon (always visible, outside HUD)
  drawGearIcon(r.gctx);

  // 14. HUD
  if (aim) {
    const currentTank = state.tanks[state.turn.currentPlayer];
    const busy = state.projectiles.activeCount > 0 || state.collapsing || state.turn.phase !== 'aiming';
    drawHUD(r.gctx, aim, busy, currentTank, hudMode);
    // Weapon name button above HUD
    if (currentTank) {
      drawWeaponName(r.gctx, currentTank);
    }
    // Weapon picker overlay
    if (picker?.open && currentTank) {
      drawWeaponPicker(r.gctx, currentTank, picker.scroll);
    }
  }

  // 14a. Launch warning overlay (covers HUD but drawn under banner/scoreboard)
  drawLaunchFx(r.gctx, dt, GAME_W, GAME_H);

  // 14b. Turn transition banner overlay
  if (showTurnBanner && state.turn.phase === 'aiming') {
    drawTurnBanner(r.gctx, state.tanks[state.turn.currentPlayer]);
  }

  // 15. Round-over scoreboard overlay
  if (state.turn.phase === 'round-over') {
    drawScoreboard(r.gctx, state.tanks, state.turn.killLog, match, roundEarnings);
  }

  // 15b. Death banner (snarky quote shown briefly after a kill)
  if (deathBanner) {
    drawDeathBanner(r.gctx, deathBanner, GAME_W, performance.now());
  }

  // 16. Final upscale to screen
  r.sctx.drawImage(r.game, 0, 0, r.screen.width, r.screen.height);
}

function drawWindArrow(ctx: CanvasRenderingContext2D, wind: number) {
  const cx = GAME_W / 2;
  const y = 16;
  const maxWind = 20;
  const maxLen = 80;
  const len = (wind / maxWind) * maxLen;

  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cx, y);
  ctx.lineTo(cx + len, y);
  if (Math.abs(len) > 4) {
    const dir = Math.sign(len);
    ctx.moveTo(cx + len, y);
    ctx.lineTo(cx + len - dir * 8, y - 5);
    ctx.moveTo(cx + len, y);
    ctx.lineTo(cx + len - dir * 8, y + 5);
  }
  ctx.stroke();
  ctx.lineWidth = 1;
}

function drawGearIcon(ctx: CanvasRenderingContext2D) {
  ctx.fillStyle = '#fff8';
  ctx.font = '28px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('\u2699', GEAR_BTN.x + GEAR_BTN.w / 2, GEAR_BTN.y + 30);
}

function drawHUD(ctx: CanvasRenderingContext2D, aim: AimState, busy: boolean, currentTank?: Tank, hudMode: HudMode = 'full') {
  // Semi-transparent background strip
  ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
  ctx.fillRect(0, HUD_Y, GAME_W, HUD_H);

  ctx.save();

  // Player name in their color (top-left of HUD)
  if (currentTank) {
    ctx.fillStyle = currentTank.color;
    ctx.font = 'bold 22px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(currentTank.name, 12, HUD_Y - 8);
  }

  // Angle row
  if (hudMode === 'full') {
    drawBtn(ctx, angleBtns.decBig, '\u25C4\u25C4');
    drawBtn(ctx, angleBtns.decSm, '\u25C4');
    drawBtn(ctx, angleBtns.incSm, '\u25BA');
    drawBtn(ctx, angleBtns.incBig, '\u25BA\u25BA');
  }

  ctx.fillStyle = '#ff0';
  ctx.font = 'bold 24px monospace';
  ctx.textAlign = 'center';
  ctx.fillText(`ANG: ${aim.angle}\u00B0`, ANGLE_LABEL_X, ANGLE_LABEL_Y);

  // Power row
  if (hudMode === 'full') {
    drawBtn(ctx, powerBtns.decBig, '\u25C4\u25C4');
    drawBtn(ctx, powerBtns.decSm, '\u25C4');
    drawBtn(ctx, powerBigBtns.decHuge, '-100');
    drawBtn(ctx, powerBigBtns.incHuge, '+100');
    drawBtn(ctx, powerBtns.incSm, '\u25BA');
    drawBtn(ctx, powerBtns.incBig, '\u25BA\u25BA');
  }

  ctx.fillStyle = '#0ff';
  ctx.font = 'bold 24px monospace';
  ctx.textAlign = 'center';
  ctx.fillText(`PWR: ${aim.power}`, POWER_LABEL_X, POWER_LABEL_Y);

  // FIRE button
  const fireColor = busy ? '#333' : '#c30';
  ctx.fillStyle = fireColor;
  ctx.fillRect(FIRE_X, FIRE_Y, FIRE_W, FIRE_H);
  ctx.strokeStyle = busy ? '#555' : '#f50';
  ctx.lineWidth = 2;
  ctx.strokeRect(FIRE_X, FIRE_Y, FIRE_W, FIRE_H);
  ctx.fillStyle = busy ? '#666' : '#fff';
  ctx.font = 'bold 30px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('FIRE', FIRE_X + FIRE_W / 2, FIRE_Y + FIRE_H / 2 + 11);
  ctx.lineWidth = 1;

  ctx.restore();
}

function drawBtn(ctx: CanvasRenderingContext2D, b: Btn, label: string) {
  ctx.fillStyle = '#444';
  ctx.fillRect(b.x, b.y, b.w, b.h);
  ctx.strokeStyle = '#888';
  ctx.strokeRect(b.x, b.y, b.w, b.h);
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 18px monospace';
  ctx.textAlign = 'center';
  ctx.fillText(label, b.x + b.w / 2, b.y + b.h - 8);
}

function drawWeaponName(ctx: CanvasRenderingContext2D, tank: Tank) {
  const b = WEAPON_NAME_BTN;
  ctx.fillStyle = 'rgba(40, 40, 40, 0.85)';
  ctx.fillRect(b.x, b.y, b.w, b.h);
  ctx.strokeStyle = '#666';
  ctx.strokeRect(b.x, b.y, b.w, b.h);
  const def = WEAPONS_BY_ID[tank.selectedWeapon];
  const name = def?.name ?? 'Baby Missile';
  const qty = tank.inventory[tank.selectedWeapon];
  const qtyStr = qty === Infinity ? 'INF' : `x${qty ?? 0}`;
  ctx.fillStyle = '#fff';
  ctx.font = '18px monospace';
  ctx.textAlign = 'left';
  ctx.fillText(`${name} ${qtyStr}`, b.x + 8, b.y + 21);
}

function drawWeaponPicker(ctx: CanvasRenderingContext2D, tank: Tank, scroll: number) {
  const p = WEAPON_PICKER;
  // Background
  ctx.fillStyle = 'rgba(10, 10, 20, 0.92)';
  ctx.fillRect(p.x, p.y, p.w, p.h);
  ctx.strokeStyle = '#ff0';
  ctx.lineWidth = 2;
  ctx.strokeRect(p.x, p.y, p.w, p.h);

  // Clip to picker area
  ctx.save();
  ctx.beginPath();
  ctx.rect(p.x, p.y, p.w, p.h);
  ctx.clip();

  const startIdx = Math.floor(scroll / WEAPON_ROW_H);
  const endIdx = Math.min(WEAPONS.length, startIdx + Math.ceil(p.h / WEAPON_ROW_H) + 1);

  for (let i = startIdx; i < endIdx; i++) {
    const w = WEAPONS[i];
    const rowY = p.y + i * WEAPON_ROW_H - scroll;
    if (rowY + WEAPON_ROW_H < p.y || rowY > p.y + p.h) continue;

    const qty = tank.inventory[w.id] ?? 0;
    const isSelected = tank.selectedWeapon === w.id;
    const isAvailable = qty > 0 || w.isFree;

    // Row background
    if (isSelected) {
      ctx.fillStyle = 'rgba(255, 255, 0, 0.15)';
      ctx.fillRect(p.x + 2, rowY, p.w - 4, WEAPON_ROW_H - 2);
    }

    // Category color dot
    const catColors: Record<string, string> = {
      missile: '#fc0', nuke: '#fff', roller: '#888', dirt: '#a64',
      splitter: '#f0f', spray: '#f0f', napalm: '#f80', digger: '#444',
      bouncer: '#0f0', tracer: '#0ff', riot: '#f44', energy: '#0ff',
    };
    ctx.fillStyle = catColors[w.category] ?? '#888';
    ctx.fillRect(p.x + 10, rowY + 16, 10, 10);

    // Name
    ctx.fillStyle = isAvailable ? '#fff' : '#555';
    ctx.font = '17px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(w.name, p.x + 30, rowY + 28);

    // Quantity
    const qtyStr = w.isFree ? 'INF' : `${qty}`;
    ctx.fillStyle = isAvailable ? '#ff0' : '#444';
    ctx.textAlign = 'right';
    ctx.fillText(qtyStr, p.x + p.w - 12, rowY + 28);
  }

  ctx.restore();
  ctx.lineWidth = 1;
}

function drawTurnBanner(ctx: CanvasRenderingContext2D, tank: Tank) {
  // Semi-transparent backdrop
  ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
  ctx.fillRect(0, 0, GAME_W, GAME_H - HUD_H);

  // Player name in color
  ctx.fillStyle = tank.color;
  ctx.font = 'bold 56px monospace';
  ctx.textAlign = 'center';
  ctx.fillText(`${tank.name}'s Turn`, GAME_W / 2, GAME_H / 2 - 40);

  // Tap prompt
  ctx.fillStyle = '#fff';
  ctx.font = '24px monospace';
  ctx.fillText('Click or press SPACE to start', GAME_W / 2, GAME_H / 2 + 30);
}

function drawScoreboard(
  ctx: CanvasRenderingContext2D,
  tanks: Tank[],
  killLog: KillEntry[],
  match?: MatchInfo,
  earnings?: number[] | null,
) {
  // Semi-transparent dark backdrop (centered on GAME_W — scales with canvas).
  const rectW = 720, rectH = 380;
  const rectX = (GAME_W - rectW) / 2;
  const rectY = 80;
  ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
  ctx.fillRect(rectX, rectY, rectW, rectH);

  // Border
  ctx.strokeStyle = '#ff0';
  ctx.lineWidth = 2;
  ctx.strokeRect(rectX, rectY, rectW, rectH);

  // Title
  const cx = GAME_W / 2;
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 24px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('ROUND OVER', cx, 120);
  if (match) {
    ctx.fillStyle = '#aaa';
    ctx.font = '14px monospace';
    ctx.fillText(`First to ${match.target} wins the match`, cx, 140);
  }

  // Per-player row (center-anchored layout)
  let y = 170;
  for (const tank of tanks) {
    ctx.fillStyle = tank.color;
    ctx.font = 'bold 18px monospace';
    ctx.textAlign = 'left';
    const kills = killLog.filter(k => k.killer === tank.id).length;
    const status = tank.alive ? 'WINNER' : 'DESTROYED';
    ctx.fillText(tank.name, cx - 280, y);
    ctx.fillStyle = '#fff';
    ctx.fillText(`K:${kills}`, cx - 100, y);
    // Win pips
    if (match && tank.id < match.wins.length) {
      const wins = match.wins[tank.id];
      const pipY = y - 10;
      for (let i = 0; i < match.target; i++) {
        const pipX = cx - 35 + i * 16;
        ctx.beginPath();
        ctx.arc(pipX, pipY, 5, 0, Math.PI * 2);
        if (i < wins) {
          ctx.fillStyle = tank.color;
          ctx.fill();
        } else {
          ctx.strokeStyle = '#666';
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }
    }
    ctx.fillStyle = tank.alive ? '#0f0' : '#f44';
    ctx.font = 'bold 16px monospace';
    ctx.fillText(status, cx + 140, y);

    // Cash + round earnings (cash is after earnings were applied)
    const gained = earnings?.[tank.id] ?? 0;
    ctx.fillStyle = '#ffd060';
    ctx.font = 'bold 14px monospace';
    ctx.textAlign = 'right';
    const cashStr = '$' + tank.cash.toLocaleString('en-US');
    ctx.fillText(cashStr, cx + 310, y);
    if (gained > 0) {
      ctx.fillStyle = '#8fc';
      ctx.font = '12px monospace';
      ctx.fillText(`+$${gained.toLocaleString('en-US')}`, cx + 310, y + 14);
    }
    ctx.textAlign = 'left';
    y += 40;
  }

  // Prompt
  ctx.fillStyle = '#ff0';
  ctx.font = '16px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('Tap to continue', cx, 430);
  ctx.lineWidth = 1;
}
